/**
 * RoamerLocationEngine.ts — WorkRoamer Roamer Location Engine™
 *
 * SINGLE RESPONSIBILITY:
 *   The ONE source of truth for the operator's current base (location).
 *   Every future location-aware feature (Weather, Housing, Roamer Score,
 *   Transport, Utilities, Community, Maps…) must read location ONLY from here
 *   and react to its `LocationChanged` event. None of them exist yet.
 *
 * ARCHITECTURE RULES:
 *   • Pure TypeScript — no React imports.
 *   • The ONLY platform capabilities used are the browser Geolocation API
 *     (navigator.geolocation), Intl (for the local timezone), and fetch
 *     (for the Nominatim geocoding provider — see below).
 *   • NO weather, hotels, maps, restaurants, housing, utilities or scoring.
 *   • localStorage persistence. Storage key: nomad:location:v1.
 *
 * GEOCODING PROVIDER — Nominatim (OpenStreetMap):
 *   searchLocations()      — synchronous, offline-only (71-entry seed dataset).
 *   searchLocationsAsync() — async; returns seed results first, then merges
 *                            live Nominatim results. Degrades gracefully to
 *                            offline-only if the network is unavailable.
 *   Nominatim endpoint: https://nominatim.openstreetmap.org/search
 *   No API key required. Usage policy: max 1 req/s (enforced by 350 ms
 *   debounce in the UI layer). Attribution: © OpenStreetMap contributors.
 *
 * FUTURE ENGINES:
 *   Subscribe with `roamerLocationEngine.onLocationChanged(fn)` (or listen for
 *   the window event `nomad:location-changed`). The engine emits — it never
 *   calls those engines directly.
 */

import {
  LOCATION_SEED,
  DEFAULT_LOCATION_ID,
  type LocationSeed,
  type LocationKind,
} from './roamerLocationData';
// Boot logging — Task #38: record location-set once ensureCurrentBase resolves.
// Import is lazy-safe: bootLogger module loads synchronously, no circular dep.
import { bootLogger } from '@/lib/bootLogger';

// ─── Types ──────────────────────────────────────────────────────────────────

/** How the current base was set — drives the UI status indicator. */
export type LocationSource = 'default' | 'gps' | 'search' | 'manual' | 'saved';

/** Which input method the user prefers. Stored in settings. */
export type LocationMode = 'gps' | 'manual';

/**
 * The canonical location object. Always represents the CURRENT BASE when read
 * from `getCurrentLocation()`. `kind` widens the seed's kind to include raw
 * coordinate pins produced by GPS / coordinate search.
 */
export interface RoamerLocation {
  locationId: string;
  latitude: number;
  longitude: number;
  city: string;
  region: string;
  state: string;
  country: string;
  countryCode: string;
  timezone: string;
  formattedAddress: string;
  kind: LocationKind | 'coordinates';
  /** Epoch ms — when this became the current base / was last set. */
  lastUpdated: number;
  /** Provenance of the value. */
  source: LocationSource;
  aliases?: string[];
}

/** Persisted user settings for the location system. */
export interface LocationSettings {
  /** "Use GPS" vs "Manual Location". */
  mode: LocationMode;
  /** The operator's designated Home Location (locationId), if any. */
  homeLocationId: string | null;
}

/** Payload emitted whenever the current base changes. */
export interface LocationChangedEvent {
  previous: RoamerLocation | null;
  current: RoamerLocation;
  source: LocationSource;
  at: number;
}

export type LocationListener = (event: LocationChangedEvent) => void;

/** Shape persisted to localStorage. Versioned so it can migrate safely. */
interface LocationStoreV1 {
  version: 1;
  current: RoamerLocation | null;
  recents: RoamerLocation[];
  favorites: RoamerLocation[];
  searchHistory: string[];
  settings: LocationSettings;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'nomad:location:v1';
const LOCATION_CHANGED_EVENT = 'nomad:location-changed';
const RECENTS_CAP = 8;
const HISTORY_CAP = 12;
/** Max distance (km) a GPS fix may be from a known base and still adopt its name. */
const GPS_SNAP_KM = 120;

// ─── Pure helpers (exported for consumers/UI) ─────────────────────────────────

/** Convert an ISO 3166-1 alpha-2 code into its flag emoji. Falls back to 📍. */
export function flagEmoji(countryCode: string): string {
  const cc = (countryCode || '').trim().toUpperCase();
  if (cc.length !== 2 || !/^[A-Z]{2}$/.test(cc)) return '📍';
  const A = 0x1f1e6;
  const base = 'A'.charCodeAt(0);
  return String.fromCodePoint(...[...cc].map(ch => A + ch.charCodeAt(0) - base));
}

/** Strip diacritics + lowercase so "medellin" matches "Medellín". */
function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/** Great-circle distance in km (Haversine). Local math — no network. */
function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const lat1 = (aLat * Math.PI) / 180;
  const lat2 = (bLat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Promote a static seed record into a runtime RoamerLocation. */
function fromSeed(seed: LocationSeed, source: LocationSource): RoamerLocation {
  return { ...seed, lastUpdated: Date.now(), source };
}

/**
 * Copy a location so internal engine state can never be mutated by a consumer.
 * `setCurrentLocation()` is the only sanctioned mutation path, so every value
 * that crosses the API boundary (getters, emitted events, stored copies) is
 * cloned. Fields are primitives except the optional `aliases` string array.
 */
function cloneLocation(loc: RoamerLocation): RoamerLocation {
  const copy = { ...loc };
  if (loc.aliases) copy.aliases = [...loc.aliases];
  return copy;
}

/**
 * Signature of a location's MATERIAL fields — identity, precise coordinates and
 * provenance. Two locations with equal signatures are, to the operator, the
 * same base set the same way. Callers use this to decide whether a base change
 * is worth committing: a GPS fix that snaps to the current city keeps that
 * city's `locationId` but carries new coordinates/source, so it is still a real
 * update. Comparing `locationId` alone would wrongly treat it as "no change".
 */
export function locationSignature(loc: RoamerLocation): string {
  return [loc.locationId, loc.latitude.toFixed(4), loc.longitude.toFixed(4), loc.source].join('|');
}

/** Parse a "lat,lng" string into coordinates, or null if not coordinate-shaped. */
function parseCoordinates(input: string): { latitude: number; longitude: number } | null {
  const m = input.trim().match(/^(-?\d{1,3}(?:\.\d+)?)\s*[,/ ]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!m) return null;
  const latitude = Number(m[1]);
  const longitude = Number(m[2]);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  return { latitude, longitude };
}

// ─── Nominatim geocoding provider ────────────────────────────────────────────

/** Minimal shape of a Nominatim JSON search result. */
interface NominatimResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  class: string;
  type: string;
  importance?: number;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
    state?: string;
    region?: string;
    country?: string;
    country_code?: string;
  };
}

/**
 * Best-effort IANA timezone for a Nominatim result.
 * US states mapped explicitly; other countries default to UTC (consumers may
 * refine later). Never throws — falls back to empty string.
 */
const US_STATE_TZ: Record<string, string> = {
  Alabama: 'America/Chicago', Alaska: 'America/Anchorage',
  Arizona: 'America/Phoenix', Arkansas: 'America/Chicago',
  California: 'America/Los_Angeles', Colorado: 'America/Denver',
  Connecticut: 'America/New_York', Delaware: 'America/New_York',
  Florida: 'America/New_York', Georgia: 'America/New_York',
  Hawaii: 'Pacific/Honolulu', Idaho: 'America/Boise',
  Illinois: 'America/Chicago', Indiana: 'America/Indiana/Indianapolis',
  Iowa: 'America/Chicago', Kansas: 'America/Chicago',
  Kentucky: 'America/New_York', Louisiana: 'America/Chicago',
  Maine: 'America/New_York', Maryland: 'America/New_York',
  Massachusetts: 'America/New_York', Michigan: 'America/Detroit',
  Minnesota: 'America/Chicago', Mississippi: 'America/Chicago',
  Missouri: 'America/Chicago', Montana: 'America/Denver',
  Nebraska: 'America/Chicago', Nevada: 'America/Los_Angeles',
  'New Hampshire': 'America/New_York', 'New Jersey': 'America/New_York',
  'New Mexico': 'America/Denver', 'New York': 'America/New_York',
  'North Carolina': 'America/New_York', 'North Dakota': 'America/Chicago',
  Ohio: 'America/New_York', Oklahoma: 'America/Chicago',
  Oregon: 'America/Los_Angeles', Pennsylvania: 'America/New_York',
  'Rhode Island': 'America/New_York', 'South Carolina': 'America/New_York',
  'South Dakota': 'America/Chicago', Tennessee: 'America/Chicago',
  Texas: 'America/Chicago', Utah: 'America/Denver',
  Vermont: 'America/New_York', Virginia: 'America/New_York',
  Washington: 'America/Los_Angeles', 'West Virginia': 'America/New_York',
  Wisconsin: 'America/Chicago', Wyoming: 'America/Denver',
  'District of Columbia': 'America/New_York',
};

function timezoneForResult(r: NominatimResult): string {
  const cc = (r.address?.country_code ?? '').toUpperCase();
  if (cc === 'US') {
    const state = r.address?.state ?? '';
    return US_STATE_TZ[state] ?? 'America/New_York';
  }
  return '';
}

/**
 * Convert a Nominatim result into a RoamerLocation-compatible record.
 * Returns null if the result lacks the minimum required fields.
 */
function nominatimToLocation(r: NominatimResult): RoamerLocation | null {
  const lat = parseFloat(r.lat);
  const lng = parseFloat(r.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const addr = r.address ?? {};
  const cityName = addr.city ?? addr.town ?? addr.village ?? addr.municipality ?? '';
  if (!cityName) return null;

  const countryCode = (addr.country_code ?? '').toUpperCase();
  const country     = addr.country ?? '';
  const state       = addr.state ?? addr.region ?? addr.county ?? '';

  // Stable slug: city + country-code, lower-kebab.
  const slug = normalize(cityName).replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') + '-' + countryCode.toLowerCase();

  // Display address: "City, State, Country" for US; "City, Country" elsewhere.
  const addrParts = [cityName, state, country].filter(Boolean);
  const formattedAddress = countryCode === 'US' ? addrParts.join(', ') : [cityName, country].filter(Boolean).join(', ');

  const kind: LocationKind =
    r.type === 'city' || r.class === 'place' && r.type === 'administrative' ? 'city'
    : r.type === 'town' ? 'town'
    : r.type === 'village' ? 'village'
    : 'city'; // default for any populated place

  return {
    locationId: slug,
    latitude: lat,
    longitude: lng,
    city: cityName,
    region: state,
    state,
    country,
    countryCode,
    timezone: timezoneForResult(r),
    formattedAddress,
    kind,
    lastUpdated: Date.now(),
    source: 'search',
  };
}

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_TIMEOUT_MS = 6000;

/**
 * Query Nominatim for populated places matching `query`.
 * Returns an empty array on any network or parse error — callers degrade to
 * offline-only results.
 */
async function fetchNominatim(query: string, limit: number): Promise<RoamerLocation[]> {
  const url = new URL(NOMINATIM_BASE);
  url.searchParams.set('q', query);
  url.searchParams.set('format', 'json');
  url.searchParams.set('limit', String(Math.min(limit + 4, 12))); // fetch extra; filter below
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('featuretype', 'settlement'); // prioritise populated places

  let signal: AbortSignal | undefined;
  try {
    signal = AbortSignal.timeout(NOMINATIM_TIMEOUT_MS);
  } catch {
    // AbortSignal.timeout not available in older browsers — proceed without
  }

  try {
    const res = await fetch(url.toString(), {
      headers: {
        'Accept': 'application/json',
        'User-Agent': 'WorkRoamer/2.0 location-search (https://workroamer.com)',
      },
      signal,
    });
    if (!res.ok) return [];
    const data = await res.json() as NominatimResult[];
    if (!Array.isArray(data)) return [];

    const results: RoamerLocation[] = [];
    for (const r of data) {
      // Only populated places — skip roads, POIs, countries, etc.
      if (r.class !== 'place' && r.class !== 'boundary') continue;
      const SKIP_TYPES = new Set(['country', 'state', 'county', 'road', 'postcode', 'suburb', 'neighbourhood', 'quarter']);
      if (SKIP_TYPES.has(r.type)) continue;
      const loc = nominatimToLocation(r);
      if (loc) results.push(loc);
    }
    return results;
  } catch {
    // Network unavailable, timeout, CORS, parse error — degrade gracefully.
    return [];
  }
}

// ─── Engine ─────────────────────────────────────────────────────────────────

class RoamerLocationEngine {
  private store: LocationStoreV1;
  private readonly listeners = new Set<LocationListener>();

  constructor() {
    this.store = this.load();
    this.ensureCurrentBase();
  }

  // ── Persistence / cache (restored automatically on construction) ────────────

  private defaultStore(): LocationStoreV1 {
    return {
      version: 1,
      current: null,
      recents: [],
      favorites: [],
      searchHistory: [],
      settings: { mode: 'manual', homeLocationId: null },
    };
  }

  private load(): LocationStoreV1 {
    if (typeof window === 'undefined') return this.defaultStore();
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return this.defaultStore();
      const parsed = JSON.parse(raw) as Partial<LocationStoreV1>;
      const base = this.defaultStore();
      return {
        version: 1,
        current: parsed.current ?? base.current,
        recents: Array.isArray(parsed.recents) ? parsed.recents : base.recents,
        favorites: Array.isArray(parsed.favorites) ? parsed.favorites : base.favorites,
        searchHistory: Array.isArray(parsed.searchHistory) ? parsed.searchHistory : base.searchHistory,
        settings: { ...base.settings, ...(parsed.settings ?? {}) },
      };
    } catch {
      return this.defaultStore();
    }
  }

  private persist(): void {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.store));
    } catch {
      /* storage full / unavailable — engine keeps working in-memory */
    }
  }

  /**
   * Read the operator persona from localStorage and extract the best available
   * location to use as the initial base. Called by ensureCurrentBase() before
   * falling back to the Bangkok system default.
   *
   * Does NOT import OperatorPersonaService to avoid circular dependencies —
   * reads the raw JSON directly from the known storage key.
   *
   * Priority: persona.currentBase (full coords) → persona.homeBase (full
   * coords) → persona.country string → first seed entry for that country.
   */
  private tryHydrateFromPersona(): RoamerLocation | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem('nomad:operator:persona:v1');
      if (!raw) {
        console.log('[LocationEngine] tryHydrateFromPersona: no persona in storage');
        return null;
      }

      const p = JSON.parse(raw) as {
        country?: string;
        homeBase?: {
          locationId?: string; city?: string; region?: string; state?: string;
          country?: string; countryCode?: string; latitude?: number;
          longitude?: number; timezone?: string; formattedAddress?: string;
        } | null;
        currentBase?: {
          locationId?: string; city?: string; region?: string; state?: string;
          country?: string; countryCode?: string; latitude?: number;
          longitude?: number; timezone?: string; formattedAddress?: string;
        } | null;
      };

      const hasCoords = (b: unknown): b is {
        locationId?: string; city: string; region?: string; state?: string;
        country: string; countryCode?: string; latitude: number; longitude: number;
        timezone?: string; formattedAddress?: string;
      } => {
        if (!b || typeof b !== 'object') return false;
        const o = b as Record<string, unknown>;
        return (
          typeof o.city === 'string' && (o.city as string).trim() !== '' &&
          typeof o.latitude  === 'number' && Number.isFinite(o.latitude)  &&
          typeof o.longitude === 'number' && Number.isFinite(o.longitude)
        );
      };

      // 1. currentBase with valid coordinates
      if (hasCoords(p.currentBase)) {
        const cb = p.currentBase;
        const inSeed = cb.locationId
          ? LOCATION_SEED.find(s => s.locationId === cb.locationId)
          : null;
        if (inSeed) {
          console.log('[LocationEngine] tryHydrateFromPersona: currentBase seed match →', inSeed.city);
          return fromSeed(inSeed, 'saved');
        }
        const loc: RoamerLocation = {
          locationId:       cb.locationId ?? `persona-current-${Date.now()}`,
          latitude:         cb.latitude,
          longitude:        cb.longitude,
          city:             cb.city,
          region:           cb.region ?? cb.state ?? '',
          state:            cb.state ?? '',
          country:          cb.country,
          countryCode:      cb.countryCode ?? '',
          timezone:         cb.timezone ?? '',
          formattedAddress: cb.formattedAddress ?? `${cb.city}, ${cb.country}`,
          kind:             'city',
          lastUpdated:      Date.now(),
          source:           'saved',
        };
        console.log('[LocationEngine] tryHydrateFromPersona: currentBase coords →', loc.city, loc.country);
        return loc;
      }

      // 2. homeBase with valid coordinates
      if (hasCoords(p.homeBase)) {
        const hb = p.homeBase;
        const inSeed = hb.locationId
          ? LOCATION_SEED.find(s => s.locationId === hb.locationId)
          : null;
        if (inSeed) {
          console.log('[LocationEngine] tryHydrateFromPersona: homeBase seed match →', inSeed.city);
          return fromSeed(inSeed, 'saved');
        }
        const loc: RoamerLocation = {
          locationId:       hb.locationId ?? `persona-home-${Date.now()}`,
          latitude:         hb.latitude,
          longitude:        hb.longitude,
          city:             hb.city,
          region:           hb.region ?? hb.state ?? '',
          state:            hb.state ?? '',
          country:          hb.country,
          countryCode:      hb.countryCode ?? '',
          timezone:         hb.timezone ?? '',
          formattedAddress: hb.formattedAddress ?? `${hb.city}, ${hb.country}`,
          kind:             'city',
          lastUpdated:      Date.now(),
          source:           'saved',
        };
        console.log('[LocationEngine] tryHydrateFromPersona: homeBase coords →', loc.city, loc.country);
        return loc;
      }

      // 3. country string → first matching seed entry (covers the common
      //    onboarding case where only country text is collected)
      const country = (p.country ?? '').trim();
      if (country) {
        const countryNorm = normalize(country);
        const seedMatch = LOCATION_SEED.find(s => normalize(s.country) === countryNorm);
        if (seedMatch) {
          console.log(
            '[LocationEngine] tryHydrateFromPersona: country string seed match →',
            seedMatch.city, 'for country:', country,
          );
          return fromSeed(seedMatch, 'saved');
        }
        console.log('[LocationEngine] tryHydrateFromPersona: country "' + country + '" not in seed — no fallback available');
      } else {
        console.log('[LocationEngine] tryHydrateFromPersona: persona has no location fields set');
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Ensure a current base is set on startup.
   *
   * Priority order (highest → lowest):
   *   1. Stored location that the user explicitly chose (source ≠ 'default').
   *      Respect it unconditionally — the user set it intentionally.
   *   2. Persona location (currentBase → homeBase → country string seed match).
   *      Covers new users who just finished onboarding and users who cleared
   *      location storage but still have a persona.
   *   3. System default (Bangkok) — only when no persona location is available.
   *
   * The stored 'default' Bangkok is overridden by the persona so that users
   * who completed onboarding before this fix also get their chosen location.
   */
  private ensureCurrentBase(): void {
    // Rule 1: User explicitly set a location → keep it.
    if (this.store.current && this.store.current.source !== 'default') {
      console.log(
        '[LocationEngine] ensureCurrentBase: keeping user-set location:',
        this.store.current.city, this.store.current.country,
        '(source:', this.store.current.source + ')',
      );
      bootLogger.record('location-set', 'ok',
        `source=${this.store.current.source} city=${this.store.current.city} country=${this.store.current.country}`);
      return;
    }

    if (this.store.current?.source === 'default') {
      console.log(
        '[LocationEngine] ensureCurrentBase: stored location is system default (' +
        this.store.current.city + ') — checking persona for override',
      );
    } else {
      console.log('[LocationEngine] ensureCurrentBase: no stored location — checking persona');
    }

    // Rule 2: Try persona before falling back to the system default.
    const personaLocation = this.tryHydrateFromPersona();
    if (personaLocation) {
      this.store.current = personaLocation;
      this.persist();
      console.log(
        '[LocationEngine] ensureCurrentBase: seeded from persona →',
        personaLocation.city, personaLocation.country,
      );
      bootLogger.record('location-set', 'ok',
        `source=persona city=${personaLocation.city} country=${personaLocation.country}`);
      return;
    }

    // Rule 3: No persona location — use system default (Bangkok).
    const seed =
      LOCATION_SEED.find(s => s.locationId === DEFAULT_LOCATION_ID) ?? LOCATION_SEED[0];
    this.store.current = fromSeed(seed, 'default');
    this.persist();
    console.log(
      '[LocationEngine] ensureCurrentBase: no persona location — using system default:',
      this.store.current.city,
    );
    bootLogger.record('location-set', 'ok',
      `source=default city=${this.store.current.city}`);
  }

  // ── Emit ────────────────────────────────────────────────────────────────────

  private emit(previous: RoamerLocation | null, current: RoamerLocation): void {
    const event: LocationChangedEvent = {
      previous: previous ? cloneLocation(previous) : null,
      current: cloneLocation(current),
      source: current.source,
      at: current.lastUpdated,
    };
    this.listeners.forEach(fn => {
      try {
        fn(event);
      } catch {
        /* a bad subscriber must not break the engine or other subscribers */
      }
    });
    // Also broadcast on the window so non-React / future engines can subscribe
    // without importing this module. (No external call — DOM event only.)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<LocationChangedEvent>(LOCATION_CHANGED_EVENT, { detail: event }));
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FUTURE-READY PUBLIC API
  // Thin accessors + the core set/emit mechanic. Deliberately contains NO
  // business logic (no weather/scoring/fetching) — future engines add that.
  // ─────────────────────────────────────────────────────────────────────────

  /** The current base. Guaranteed non-null after construction. */
  getCurrentLocation(): RoamerLocation {
    if (!this.store.current) this.ensureCurrentBase();
    return cloneLocation(this.store.current as RoamerLocation);
  }

  /**
   * Change the current base. Accepts a full RoamerLocation or a static seed.
   * Updates the stored object, refreshes recents, persists, and emits
   * `LocationChanged`. This is the ONLY mutation path for the current base.
   */
  setCurrentLocation(
    location: RoamerLocation | LocationSeed,
    source: LocationSource = 'manual',
  ): RoamerLocation {
    const previous = this.store.current;
    const next: RoamerLocation = {
      ...cloneLocation(location as RoamerLocation),
      lastUpdated: Date.now(),
      // Preserve an explicit source already on the object (e.g. 'gps'); else use arg.
      source: (location as RoamerLocation).source ?? source,
    };

    this.store.current = next;
    this.pushRecent(next);
    this.persist();
    this.emit(previous, next);
    return cloneLocation(next);
  }

  getCoordinates(): { latitude: number; longitude: number } {
    const c = this.getCurrentLocation();
    return { latitude: c.latitude, longitude: c.longitude };
  }

  getTimezone(): string {
    return this.getCurrentLocation().timezone;
  }

  getCountry(): string {
    return this.getCurrentLocation().country;
  }

  getCountryCode(): string {
    return this.getCurrentLocation().countryCode;
  }

  getCity(): string {
    return this.getCurrentLocation().city;
  }

  getRegion(): string {
    return this.getCurrentLocation().region;
  }

  /** Subscribe to base changes. Returns an unsubscribe function. */
  subscribe(listener: LocationListener): () => void {
    this.listeners.add(listener);
    return () => this.unsubscribe(listener);
  }

  /** Alias of subscribe(), named per the engine contract. */
  onLocationChanged(listener: LocationListener): () => void {
    return this.subscribe(listener);
  }

  unsubscribe(listener: LocationListener): void {
    this.listeners.delete(listener);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // LOCATION SEARCH SERVICE
  // ─────────────────────────────────────────────────────────────────────────

  /** All bases known to the offline dataset (as selectable locations). */
  getAllLocations(): RoamerLocation[] {
    return LOCATION_SEED.map(s => fromSeed(s, 'search'));
  }

  /**
   * Autocomplete over the offline dataset. Also understands a raw "lat,lng"
   * query, returning a coordinate pin (snapped to the nearest known base name).
   * Ranking: city prefix > alias/region/country prefix > substring match.
   */
  searchLocations(query: string, limit = 8): RoamerLocation[] {
    const q = normalize(query);
    if (!q) return [];

    // Coordinate search — authoritative lat/lng beats any name.
    const coords = parseCoordinates(query);
    if (coords) {
      return [this.resolveCoordinates(coords.latitude, coords.longitude, 'search')];
    }

    const scored: { seed: LocationSeed; score: number }[] = [];
    for (const seed of LOCATION_SEED) {
      const city = normalize(seed.city);
      const region = normalize(seed.region);
      const country = normalize(seed.country);
      const cc = normalize(seed.countryCode);
      const aliases = (seed.aliases ?? []).map(normalize);

      let score = -1;
      if (city.startsWith(q)) score = 100;
      else if (aliases.some(a => a.startsWith(q))) score = 90;
      else if (region.startsWith(q) || country.startsWith(q) || cc === q) score = 80;
      else if (city.includes(q)) score = 60;
      else if (aliases.some(a => a.includes(q)) || region.includes(q) || country.includes(q)) score = 40;

      if (score >= 0) scored.push({ seed, score });
    }

    scored.sort((a, b) => b.score - a.score || a.seed.city.localeCompare(b.seed.city));
    return scored.slice(0, limit).map(s => fromSeed(s.seed, 'search'));
  }

  /**
   * Async version of searchLocations. Immediately returns offline-seed results,
   * then fetches from Nominatim and returns the merged deduplicated set.
   *
   * Callers should debounce (≥ 350 ms) to respect Nominatim's rate limit of
   * 1 request per second.
   *
   * Degrades gracefully: if Nominatim is unreachable the offline-seed results
   * are returned as-is without throwing.
   */
  async searchLocationsAsync(query: string, limit = 8): Promise<RoamerLocation[]> {
    const q = query.trim();
    if (!q) return [];

    // Coordinate search — fully offline, no provider needed.
    const coords = parseCoordinates(q);
    if (coords) {
      return [this.resolveCoordinates(coords.latitude, coords.longitude, 'search')];
    }

    // 1. Offline seed (instant — already in memory).
    const local = this.searchLocations(q, limit);

    // 2. Live Nominatim results.
    const remote = await fetchNominatim(q, limit);

    // 3. Merge: seed results first (they carry curated metadata), then remote.
    //    Deduplicate by normalised city + countryCode to avoid near-duplicates.
    const seen = new Set(local.map(l => normalize(l.city) + l.countryCode));
    const merged: RoamerLocation[] = [...local];
    for (const r of remote) {
      const key = normalize(r.city) + r.countryCode;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(r);
      }
    }

    // Sort: seed results by their existing score order, remote by Nominatim
    // importance (already sorted by Nominatim API). Limit to requested count.
    return merged.slice(0, limit);
  }

  // ── Recents ─────────────────────────────────────────────────────────────────

  getRecents(): RoamerLocation[] {
    return this.store.recents.map(cloneLocation);
  }

  private pushRecent(location: RoamerLocation): void {
    const deduped = this.store.recents.filter(r => r.locationId !== location.locationId);
    deduped.unshift(cloneLocation(location));
    this.store.recents = deduped.slice(0, RECENTS_CAP);
  }

  clearRecents(): void {
    this.store.recents = [];
    this.persist();
  }

  // ── Favorites ─────────────────────────────────────────────────────────────

  getFavorites(): RoamerLocation[] {
    return this.store.favorites.map(cloneLocation);
  }

  isFavorite(locationId: string): boolean {
    return this.store.favorites.some(f => f.locationId === locationId);
  }

  addFavorite(location: RoamerLocation): void {
    if (this.isFavorite(location.locationId)) return;
    this.store.favorites = [cloneLocation(location), ...this.store.favorites];
    this.persist();
  }

  removeFavorite(locationId: string): void {
    this.store.favorites = this.store.favorites.filter(f => f.locationId !== locationId);
    this.persist();
  }

  /** Toggle favorite state; returns the new state (true = now a favorite). */
  toggleFavorite(location: RoamerLocation): boolean {
    if (this.isFavorite(location.locationId)) {
      this.removeFavorite(location.locationId);
      return false;
    }
    this.addFavorite(location);
    return true;
  }

  // ── Search history ──────────────────────────────────────────────────────────

  getSearchHistory(): string[] {
    return [...this.store.searchHistory];
  }

  addSearchHistory(query: string): void {
    const q = query.trim();
    if (!q) return;
    const deduped = this.store.searchHistory.filter(h => normalize(h) !== normalize(q));
    deduped.unshift(q);
    this.store.searchHistory = deduped.slice(0, HISTORY_CAP);
    this.persist();
  }

  clearSearchHistory(): void {
    this.store.searchHistory = [];
    this.persist();
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GPS (browser Geolocation API only — no reverse geocoding over the network)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Resolve the device's current position to a RoamerLocation.
   * Steps (all local): read coords from navigator.geolocation → read the
   * browser's IANA timezone from Intl → name the place by snapping to the
   * nearest known base in the offline dataset. Does NOT set the current base;
   * the caller decides (so the picker can preview, then Save).
   */
  useCurrentGps(): Promise<RoamerLocation> {
    return new Promise((resolve, reject) => {
      if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
        reject(new Error('Location services are not available on this device.'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        pos => resolve(this.resolveCoordinates(pos.coords.latitude, pos.coords.longitude, 'gps')),
        err => reject(new Error(this.gpsErrorMessage(err))),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
      );
    });
  }

  /**
   * Turn raw coordinates into a RoamerLocation using ONLY local data:
   * nearest-known-base snap for naming, Intl for timezone. If nothing is close
   * enough, returns an honest coordinate pin instead of guessing a city.
   */
  resolveCoordinates(latitude: number, longitude: number, source: LocationSource): RoamerLocation {
    const localTz =
      (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || '';

    let nearest: LocationSeed | null = null;
    let nearestKm = Infinity;
    for (const seed of LOCATION_SEED) {
      const d = distanceKm(latitude, longitude, seed.latitude, seed.longitude);
      if (d < nearestKm) {
        nearestKm = d;
        nearest = seed;
      }
    }

    if (nearest && nearestKm <= GPS_SNAP_KM) {
      // Adopt the known base's naming, but keep the precise device coordinates.
      return {
        ...nearest,
        latitude,
        longitude,
        timezone: localTz || nearest.timezone,
        lastUpdated: Date.now(),
        source,
      };
    }

    // No known base nearby — an honest coordinate pin (no fabricated city).
    return {
      locationId: `coord-${latitude.toFixed(4)}-${longitude.toFixed(4)}`,
      latitude,
      longitude,
      city: 'Current Location',
      region: '',
      state: '',
      country: '',
      countryCode: '',
      timezone: localTz,
      formattedAddress: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
      kind: 'coordinates',
      lastUpdated: Date.now(),
      source,
    };
  }

  /**
   * Async variant of resolveCoordinates.
   *
   * Tries the offline seed-snap first (instantaneous). If the coordinates fall
   * outside any known base's 120 km radius, falls back to Nominatim reverse
   * geocoding so real cities — including US metros not in the curated seed —
   * resolve to a proper name ("Tucson, Arizona") instead of "Current Location".
   *
   * Always returns a valid RoamerLocation; degrades to an honest coordinate
   * pin only when both the offline snap AND the network call fail.
   *
   * LocationRefreshService (and any other async caller) should use this in
   * preference to the synchronous resolveCoordinates.
   */
  async reverseGeocodeCoordinates(
    latitude: number,
    longitude: number,
    source: LocationSource,
  ): Promise<RoamerLocation> {
    // 1. Offline snap — instant, no network.
    const snapped = this.resolveCoordinates(latitude, longitude, source);
    if (snapped.kind !== 'coordinates') return snapped;

    // 2. Nominatim reverse geocoding — network fallback.
    try {
      const url = new URL('https://nominatim.openstreetmap.org/reverse');
      url.searchParams.set('format', 'json');
      url.searchParams.set('lat', String(latitude));
      url.searchParams.set('lon', String(longitude));
      url.searchParams.set('addressdetails', '1');
      let signal: AbortSignal | undefined;
      try { signal = AbortSignal.timeout(NOMINATIM_TIMEOUT_MS); } catch { /* older browsers */ }
      const res = await fetch(url.toString(), {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'WorkRoamer/2.0 reverse-geocode (https://workroamer.com)',
        },
        signal,
      });
      if (!res.ok) throw new Error(`Nominatim reverse ${res.status}`);
      const data = await res.json() as NominatimResult;
      const loc = nominatimToLocation(data);
      if (loc) {
        // Keep the user's precise GPS coordinates; adopt the city / state /
        // country from the API so the header shows "Tucson, Arizona" not a pin.
        return { ...loc, latitude, longitude, source, lastUpdated: Date.now() };
      }
    } catch {
      /* Network unavailable / timeout — degrade to coordinate pin below */
    }

    // 3. Honest coordinate pin — no fabricated city name.
    return snapped;
  }

  private gpsErrorMessage(err: GeolocationPositionError): string {
    switch (err.code) {
      case err.PERMISSION_DENIED:
        return 'Location permission was denied. You can search for your base instead.';
      case err.POSITION_UNAVAILABLE:
        return 'Your position is currently unavailable. Try again or search manually.';
      case err.TIMEOUT:
        return 'Getting your location timed out. Try again or search manually.';
      default:
        return 'Could not get your location. Search for your base instead.';
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // USER SETTINGS
  // ─────────────────────────────────────────────────────────────────────────

  getSettings(): LocationSettings {
    return { ...this.store.settings };
  }

  updateSettings(patch: Partial<LocationSettings>): LocationSettings {
    this.store.settings = { ...this.store.settings, ...patch };
    this.persist();
    return this.getSettings();
  }

  getHomeLocation(): RoamerLocation | null {
    const id = this.store.settings.homeLocationId;
    if (!id) return null;
    const fav = this.store.favorites.find(f => f.locationId === id);
    if (fav) return cloneLocation(fav);
    const seed = LOCATION_SEED.find(s => s.locationId === id);
    return seed ? fromSeed(seed, 'saved') : null;
  }

  setHomeLocation(location: RoamerLocation | null): void {
    this.updateSettings({ homeLocationId: location?.locationId ?? null });
  }
}

// ─── Singleton — the app's one location source of truth ───────────────────────

export const roamerLocationEngine = new RoamerLocationEngine();

// Re-export seed types so consumers import everything location from one place.
export type { LocationSeed, LocationKind } from './roamerLocationData';
